import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000; // 30 s — cheap admin count query, no need to hammer
const STORAGE_LAST_TOTAL = "sb_inbox_last_total";
const STORAGE_MUTE = "sb_inbox_mute";
const STORAGE_LAST_ALERT = "sb_inbox_last_alert_at";
const MIN_ALERT_INTERVAL_MS = 45_000; // avoid double-chime if two windows are open

/**
 * useApprovalNotifier
 * -------------------
 * Silent background hook that polls the admin inbox counts and:
 *   1. Plays a short two-tone chime via Web Audio API (no static file)
 *      when the total pending count strictly increases.
 *   2. Fires a native browser notification (needs one-time permission).
 *   3. Persists the last-seen total in localStorage so a page reload
 *      doesn't re-fire alerts for items that were already there.
 *
 * Mounted globally inside `ProtectedAdminRoute` so it runs on every admin
 * page — you don't need the Inbox open to hear the chime.
 */
const useApprovalNotifier = (enabled = true) => {
  const [counts, setCounts] = useState({ total: 0, clients: 0, guests: 0, bookings: 0 });
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(STORAGE_MUTE) === "1"; } catch { return false; }
  });
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const audioCtxRef = useRef(null);
  const inflightRef = useRef(false);

  const playChime = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      // Two soft sine bells — 660 Hz then 990 Hz — with quick fade out
      [
        { freq: 660, start: now, dur: 0.18 },
        { freq: 990, start: now + 0.14, dur: 0.22 },
      ].forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.15, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur + 0.02);
      });
    } catch (err) {
      // AudioContext failing pre-interaction is expected browser behaviour,
      // but surface unexpected errors for easier debugging.
      // eslint-disable-next-line no-console
      console.warn("chime playback failed", err);
    }
  }, []);

  const showBrowserNotification = useCallback((delta, next) => {
    try {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      const bits = [];
      if (next.clients) bits.push(`${next.clients} client${next.clients === 1 ? "" : "s"}`);
      if (next.guests) bits.push(`${next.guests} guest${next.guests === 1 ? "" : "s"}`);
      if (next.bookings) bits.push(`${next.bookings} booking${next.bookings === 1 ? "" : "s"}`);
      const notif = new Notification(
        `${delta} new item${delta === 1 ? "" : "s"} waiting`,
        {
          body: bits.length ? bits.join(" · ") : "Head to the Admin Inbox.",
          tag: "sb-inbox",
          icon: "/brand/sb-logo.svg",
          silent: false,
        },
      );
      notif.onclick = () => {
        try { window.focus(); } catch (_e) { /* noop */ }
        window.location.href = "/admin/inbox";
        notif.close();
      };
    } catch {
      // ignore
    }
  }, []);

  const alertIfNew = useCallback(
    (next) => {
      if (muted) return;
      let last = 0;
      try {
        last = parseInt(localStorage.getItem(STORAGE_LAST_TOTAL) || "0", 10) || 0;
      } catch { last = 0; }
      const delta = next.total - last;
      if (delta > 0) {
        // Rate-limit — if we already alerted very recently (another tab
        // beat us to it), skip.
        const lastAlertAt = parseInt(localStorage.getItem(STORAGE_LAST_ALERT) || "0", 10) || 0;
        if (Date.now() - lastAlertAt > MIN_ALERT_INTERVAL_MS) {
          playChime();
          showBrowserNotification(delta, next);
          try { localStorage.setItem(STORAGE_LAST_ALERT, String(Date.now())); } catch { /* noop */ }
        }
      }
      try { localStorage.setItem(STORAGE_LAST_TOTAL, String(next.total)); } catch { /* noop */ }
    },
    [muted, playChime, showBrowserNotification],
  );

  const poll = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const token = localStorage.getItem("sb_admin_token") || "";
      if (!token) return;
      const { data } = await axios.get(`${API}/admin/inbox/pending-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCounts(data);
      alertIfNew(data);
    } catch {
      // Silent — 401 means the token expired; the normal route guard handles it
    } finally {
      inflightRef.current = false;
    }
  }, [alertIfNew]);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, poll]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "default";
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      return p;
    } catch {
      return "denied";
    }
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_MUTE, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  }, []);

  return { counts, permission, muted, requestPermission, toggleMute, refresh: poll };
};

export default useApprovalNotifier;
